import {
  LatLng,
  PlantDetailsCardProps,
  PlantationType,
  RecommendationsChartsProps,
} from '../../components';
import { useQueries } from '@tanstack/react-query';
import { useSiflorApi } from '../../providers/SiflorApiProvider/useSiflorApi';

const months = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

export const useRecommendation = ({
  latLng,
  plantationType,
}: {
  latLng?: LatLng;
  plantationType: PlantationType;
}) => {
  const { siflorApi } = useSiflorApi();

  const [
    monoRecommendations,
    iLPFRecommendations,
    /**
     * First chart.
     */
    precipitations,
    averageTemperatures,
    minimumTemperatures,
    maximumTemperatures,
    /**
     * Second chart.
     */
    waterVolumes,
    /**
     * Third chart.
     */
    soilWaterStorages,
    /**
     * Fourth chart.
     */
    evapotranspiration,
    /**
     * Fifth chart.
     */
    globalSolarRadiations,
    photosyntheticallyActiveRadiations,
  ] = useQueries({
    queries: [
      'getMonoRecommendations',
      'getILPFRecommendations',
      'getPrecipitations',
      'getAverageTemperaures',
      'getMinimumTemperaures',
      'getMaximumTemperaures',
      'getWaterVolumes',
      'getSoilWaterStorages',
      'getEvapotranspirations',
      'getGlobalSolarRadiations',
      'getPhotosyntheticallyActiveRadiations',
    ].map((method) => {
      return {
        /**
         * Fetch query whenever `latLng` and `plantationType` change.
         */
        queryKey: [method, JSON.stringify(latLng), plantationType],
        queryFn: async () => {
          if (!latLng) {
            return null;
          }

          const fn = siflorApi?.[method];

          if (!fn) {
            return null;
          }

          return fn(latLng?.lat, latLng?.lng);
        },
        /**
         * https://stackoverflow.com/a/67041725/8786986
         */
        staleTime: Number.POSITIVE_INFINITY,
      };
    }),
  });

  const recommendations: PlantDetailsCardProps['recommendations'] = (() => {
    if (
      iLPFRecommendations.status !== 'success' ||
      monoRecommendations.status !== 'success'
    ) {
      return [];
    }

    const plantationTypeRecommendations: Record<string, any> | undefined =
      plantationType === 'iLPF'
        ? (iLPFRecommendations.data as any)
        : (monoRecommendations.data as any);

    return [
      {
        label: 'Apto Superior',
        ...plantationTypeRecommendations?.aptoSuperior,
      },
      {
        label: 'Apto',
        ...plantationTypeRecommendations?.apto,
      },
      {
        label: 'Apto com Ressalvas',
        ...plantationTypeRecommendations?.aptoRessalva,
      },
      {
        label: 'Inapto',
        ...plantationTypeRecommendations?.inapto,
      },
    ];
  })();

  const recommendationsChartsProps: RecommendationsChartsProps = (() => {
    if (
      ![
        precipitations,
        averageTemperatures,
        minimumTemperatures,
        maximumTemperatures,
        waterVolumes,
        soilWaterStorages,
        evapotranspiration,
        globalSolarRadiations,
        photosyntheticallyActiveRadiations,
      ]
        .map(({ status }) => {
          return status;
        })
        .includes('success')
    ) {
      return {};
    }

    const tmpRecommendationChartProps = months.reduce(
      (acc, cur, index) => {
        const monthIndex = index + 1;
        const monthLabel = cur.charAt(0);

        if (
          precipitations?.data ||
          averageTemperatures?.data ||
          minimumTemperatures?.data ||
          maximumTemperatures?.data
        ) {
          acc.dataChartTemperature?.push({
            month: monthIndex,
            monthLabel,
            precipitation: precipitations?.data?.[monthIndex] || 0,
            tempMe: averageTemperatures?.data?.[monthIndex] || 0,
            tempMax: maximumTemperatures?.data?.[monthIndex] || 0,
            tempMin: minimumTemperatures?.data?.[monthIndex] || 0,
          });
        }

        if (waterVolumes?.data) {
          acc.dataChartHydricBalance?.push({
            month: monthIndex,
            monthLabel,
            hydricVolume: waterVolumes?.data?.[monthIndex] || 0,
          });
        }

        if (soilWaterStorages?.data) {
          acc.dataChartSoilWaterStorage?.push({
            month: monthIndex,
            monthLabel,
            soilWaterStorageVolume: soilWaterStorages?.data?.[monthIndex] || 0,
          });
        }
        if (evapotranspiration?.data) {
          acc.dataChartEvapotranspiration?.push({
            month: monthIndex,
            monthLabel,
            evapotranspirationVolume:
              evapotranspiration?.data?.[monthIndex] || 0,
          });
        }

        if (
          photosyntheticallyActiveRadiations?.data ||
          globalSolarRadiations?.data
        ) {
          acc.dataChartSolarRadiation?.push({
            month: monthIndex,
            monthLabel,
            photosyntheticallyActiveRadiation:
              photosyntheticallyActiveRadiations?.data?.[monthIndex] || 0,
            globalSolarRadiation:
              globalSolarRadiations?.data?.[monthIndex] || 0,
          });
        }

        return acc;
      },
      {
        dataChartTemperature: [],
        dataChartHydricBalance: [],
        dataChartSoilWaterStorage: [],
        dataChartEvapotranspiration: [],
        dataChartSolarRadiation: [],
      } as RecommendationsChartsProps
    );

    return tmpRecommendationChartProps;
  })();

  return { recommendationsChartsProps, recommendations };
};
